<template>
    <div>
        <div :class="styleClasses.wrapper" v-if="hasOptions">
            <label class="block text-body-6 font-medium text-default-1" :class="[props.labelPosition ? 'mb-2' : '']">
                {{ label }}
                <span v-if="required" class="text-interactive-3 text-body-4">*</span>
            </label>
            <div :class="[styleClasses.inputError, styleClasses.inputWidth]">

                <Variant v-if="isDisabled && mode === 'multiple'" :options="modelValue"
                         placeholder="Click to view selected options" class="mb-6" :is-disabled="true"/>
                <Multiselect v-else-if="isDisabled &&  mode !== 'multiple' || !isDisabled"
                             ref="selectRef"
                             :modelValue="modelValue"
                             @update:modelValue="$emit('update:modelValue', $event)"
                             :options="filteredList"
                             :placeholder="placeholder"
                             :label="selectLabel"
                             :trackBy="selectTrackBy"
                             :valueProp="selectValueProp"
                             :classes="classes"
                             :mode="mode"
                             :closeOnSelect="closeOnSelect"
                             :hideSelected="hideSelected"
                             :noResultsText="noResultsText"
                             :loading="loading"
                             :disabled="isDisabled"
                             :open-direction="openDirection"
                             :create-option="false"
                             :canClear="!isInline"
                >
                    <template v-slot:singlelabel="{ value, disabled }">
                        <div class="multiselect-single-label" :class="isInline && 'p-0'">
                            <div
                                    class=" px-2 py-0.5 rounded font-normal text-body-7 capitalize text"
                                    :class="[disabled ? 'is-disabled': '', typeStyle(value[selectValueProp])]"
                            >
                                {{ value[selectLabel] }}

                            </div>
                        </div>
                    </template>
                    <template v-slot:tag="{ option, handleTagRemove, disabled }">
                        <div
                            class="multiselect-tag px-2 py-0.5 rounded-[6px] tag font-normal text-body-7 capitalize text"
                            :class="[disabled ? 'is-disabled': '', typeStyle(option[selectValueProp])]"
                        >
                            {{ option[selectLabel] }}
                            <div
                                    v-if="!disabled && !isInline"
                                    class="multiselect-tag-remove"
                                    @mousedown.prevent="clearSearch(handleTagRemove, $event, option)"
                            >
                                <span  class="multiselect-tag-remove-icon"></span>
                            </div>
                        </div>
                    </template>
                    <template v-if="!isInline" v-slot:beforelist>
                        <div class="px-4 mt-2 mb-2 relative">
                            <input type="text" v-model="search" placeholder="Search" class="min-h-[40px] w-full border-l-0 border-t-0 border-r-0 !ring-0 !focus:shadow-none !shadow-none border-interactive-9 placeholder:text-default-3 pl-0 pr-6 text-body-6 text-default-1" />
                            <img src="../../../assets/icons/search.svg" alt="" class="absolute top-1/2 right-4 left-auto -translate-y-1/2" />
                        </div>
                    </template>
                    <template  v-slot:option="{ option, isSelected }">
                        <Checkbox v-if="hasCheckbox" :model-value="isSelected(option)" @click.prevent></Checkbox>
                        <div class="cursor-default select-none relative -mb-px capitalize px-1 py-0.5 rounded-[6px]"
                              :class="[hasCheckbox ? 'pl-1' : '', typeStyle(option[selectValueProp])]">{{ option[props.selectLabel] }}</div>
                    </template>
                </Multiselect>
            </div>
        </div>
        <p v-if="hasError" class="text-body-7 font-medium mt-1" :class="styleClasses.errorMessage"> {{ hintText }} </p>
    </div>
</template>
<script setup>
import Multiselect from '@vueform/multiselect'
import {ref, computed, watch, defineProps, defineEmits} from 'vue'
import Checkbox from "@/components/core/Checkbox";
import Variant from "@/components/core/Select/Variant.vue";

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Not selected'
    },
    options: {
        type: Array,
        required: true,
        default: () => []
    },
    labelPosition: {
        type: String,
        validator: function (value) {
            return ['left', 'top'].indexOf(value) !== -1;
        },
        default: 'top',
    },
    modelValue: {
        required: true,
    },
    hintText: {
        type: String,
        default: null
    },
    hasError: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false,
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    selectTrackBy: {
        type: String,
        default: undefined
    },
    selectLabel: {
        type: String,
        default: 'label'
    },
    selectValueProp: {
        type: String,
        default: 'name',
    },
    hasCheckbox: {
        type: Boolean,
        default: true,
    },
    isEditable: {
        type: Boolean,
        default: false,
    },
    closeOnSelect: {
        type: Boolean,
        default: false,
    },
    noResultsText: {
        type: String,
        default: 'No results found'
    },
    loading: {
        type: Boolean,
        default: false,
    },
    openDirection: {
        type: String,
        default: 'bottom'
    },
    mode: {
        type: String,
        default: ''
    },
    isInline: {
        type: Boolean,
        default: false
    },
    hasLoadOptions: {
        type: Boolean,
        default: true
    },
})

const classes = ref({
    container: props.isInline ? 'multiselect border-none text-body-6 placeholder:text-default-4 bg-white min-h-[40px] w-full pl-0 rounded text-left hover:border hover:border-primary-1' : 'multiselect text-body-6 placeholder:text-default-4 bg-white border border-interactive-9 min-h-[40px] w-full rounded text-left focus:outline-none focus:ring-transparent focus:border-primary-1 hover:border hover:border-primary-1',
    search: 'w-full min-h-[40px] absolute border-0 inset-0 outline-none focus:ring-0 appearance-none box-border text-base font-sans bg-transparent rounded pl-3.5 rtl:pl-0 rtl:pr-3.5 hover:border-primary-1 hover:border',
    option: 'flex items-center justify-start box-border text-left cursor-pointer leading-snug pl-4 pr-2 py-2 focus:text-primary-1 text-body-6 text-default-1',
    optionPointed: 'hover:text-primary-1 cursor-pointer hover:bg-background-3',
    optionSelected: 'bg-white',
    optionDisabled: 'text-gray-300 cursor-not-allowed',
    optionSelectedPointed: 'bg-background-3 opacity-90',
    optionSelectedDisabled: 'text-green-100 bg-green-500 bg-opacity-50 cursor-not-allowed',
    multiselect: props.isInline ? '!border-none' : 'border border-primary-9',
    containerActive: !props.isInline && 'border-primary-1',
})

const selectRef = ref(null);

const search = ref('');

const emit = defineEmits(['update:modelValue', 'loadOptions', 'selectAll']);

const hideSelected = false;

watch(props.options, () => {
    selectRef.value.refreshOptions();
}, {deep: true})


const hasOptions = computed(() => {
    return props.options != null
})

const styleClasses = computed(() => ({
    wrapper: {
        'flex items-center': props.labelPosition === 'left',
    },
    inputWidth: {
        'flex-1 ml-2': props.labelPosition === 'left',
    },
    inputError: {
        'border rounded-[6px] border-interactive-3 focus:border-interactive-3 hover:border-interactive-3 white-inset': props.hasError,
        'border-interactive-8 focus:border-primary-1 hover:border-primary-1': !props.hasError && !props.isDisabled && !props.isInline,
    },
    errorMessage: {
        'text-interactive-3': props.hasError,
        'text-gray-500': !props.hasError
    }
}));

function typeStyle(type) {
    switch (type) {
    case 'user-registration':
        return 'bg-notification-secondary-8 text-notification-primary-8';
    case 'import':
        return 'bg-notification-secondary-4 text-notification-primary-4';
    case 'groups':
        return 'bg-notification-secondary-1 text-notification-primary-1';
    case 'emails':
        return 'bg-notification-secondary-5 text-notification-primary-5';
    case 'delivered':
    case 'open':
    case 'click':
    case 'approved':
        return 'bg-tag-secondary-9 text-tag-primary-9 hover:bg-tag-primary-9 hover:text-tag-secondary-9';
    case 'deferred':
    case 'processed':
    case 'deactivated':
        return 'bg-tag-secondary-1 text-tag-primary-1 hover:bg-tag-primary-1 hover:text-tag-secondary-1';
    case 'dropped':
    case 'bounce':
    case 'invited':
        return 'bg-tag-secondary-2 text-tag-primary-2 hover:bg-tag-primary-2 hover:text-tag-secondary-2';
    case 'pending':
        return 'bg-notification-secondary-2 text-notification-primary-2 hover:bg-notification-primary-2 hover:text-notification-secondary-2';
    case 'nonactive':
        return 'bg-tag-secondary-6 text-tag-primary-6 hover:bg-tag-primary-6 hover:text-tag-secondary-6';
    default:
        return 'text-default-1';
    }
}

const filteredList = computed(() => {
    return props.options.filter(option => {
        return option[props.selectLabel].toLowerCase().includes(search.value.toLowerCase())
    })
});

function clearSearch(handleTagRemove, event, option) {
    handleTagRemove(option, event)
    search.value = '';
}

</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style lang="scss">
.multiselect {
    &.is-disabled {
        background: white;

        .multiselect-caret {
            display: none;
        }
    }
}

.multiselect-dropdown {
    max-height: 400px;
}

.multiselect-caret, .multiselect-clear-icon {
    z-index: 1;
}

.white-inset {
    -webkit-box-shadow: inset 0 0 0 10px #FFFFFF;
    box-shadow: inset 0 0 0 10px #FFFFFF;
}

.multiselect-tag.tag i:before {
    color: #ffffff;
    border-radius: 50%;;
}
</style>
